import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import { Band } from "shared/assets/icons";
import { Card, IActionDropdownItem, Table, TableColumn } from "shared/components";
import {
  customSubscriptionRequestsSelector,
  dataMetaSelector,
  disptachGetCustomSubscriptionRequests,
} from "Bands/state";
import { IAPIState, ICardTheme, IStatus, ITheme } from "shared/types";
import { BAND_ACTIONS, IBandCustomSubscriptionRequest } from "Bands/types";
import { CreateCustomSubscriptionModal } from "Bands/containers";
import { CancelRequestModal } from "Bands/pages/company-dashboard/containers/CancelRequestModal";
import { addTemporaryToast } from "shared/components/Toast";

const { customSubscriptions: customSubscriptionsCopy } = copy.adminDashboard;
const customCopy = copy.subscription.custom;

export const CustomSubscriptionsRequestsTable = () => {
  const [customSubscriptionModal, setCustomSubscriptionModal] = useState<{
    show: boolean;
    subscription: IBandCustomSubscriptionRequest | null;
  }>({ show: false, subscription: null });

  const [showDeleteModal, setShowDeleteModal] = useState<IBandCustomSubscriptionRequest | null>(
    null
  );

  const dispatch = useDispatch();

  useEffect(() => {
    disptachGetCustomSubscriptionRequests({ dispatch });
  }, [dispatch]);

  // delete request
  const { api: deleteApiMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.deleteCustomSubscriptionRequest)
  );

  const customSubscriptionRequests = useSelector(customSubscriptionRequestsSelector);

  const getRowActions = useCallback(
    (subscriptionId: string): IActionDropdownItem[] => {
      const request = customSubscriptionRequests.find(({ id }) => id === subscriptionId);

      if (!request) return [];

      const actions: IActionDropdownItem[] = [
        {
          label: customSubscriptionsCopy.actions.addCustomSubscription,
          handleClick: () => {
            setCustomSubscriptionModal({
              show: true,
              subscription: request,
            });
          },
          dataTestId: "add_custom_subscription_action",
        },
        {
          label: copy.form.cancel,
          handleClick: () => setShowDeleteModal(request),
          status: IStatus.error,
        },
      ];

      return actions;
    },
    [customSubscriptionRequests]
  );

  useEffect(() => {
    if (deleteApiMeta?.status === IAPIState.succeeded) {
      setShowDeleteModal(null);

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: customCopy.messages.canceled,
        })
      );
    }
  }, [dispatch, deleteApiMeta]);

  // Check URL for requestId param and show modal if found
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const requestId = params.get("requestId");

    if (requestId) {
      const request = customSubscriptionRequests.find(({ id }) => id === requestId);
      if (request) {
        setCustomSubscriptionModal({
          show: true,
          subscription: request,
        });
      }
    }
  }, [customSubscriptionRequests]);

  return (
    <>
      {customSubscriptionModal.show && customSubscriptionModal.subscription && (
        <CreateCustomSubscriptionModal
          handleClose={() => setCustomSubscriptionModal({ show: false, subscription: null })}
          subscription={customSubscriptionModal.subscription}
        />
      )}
      {showDeleteModal && (
        <CancelRequestModal
          request={showDeleteModal}
          handleClose={() => setShowDeleteModal(null)}
        />
      )}
      <Card title={customSubscriptionsCopy.title} icon={Band} theme={ICardTheme.secondary}>
        <Table
          dataTestId="custom_subscription_requests"
          data={customSubscriptionRequests}
          theme={ITheme.secondary}
          columns={columns}
          getRowActions={getRowActions}
          emptyCopy={customSubscriptionsCopy.empty}
        />
      </Card>
    </>
  );
};

const columns: TableColumn[] = [
  {
    Header: customSubscriptionsCopy.table.band,
    accessor: "bands[0].title",
  },
  {
    Header: customSubscriptionsCopy.table.createdBy,
    accessor: "createdBy",
    Cell: ({ value }) => `${value.firstname} ${value.lastname}`,
  },
];
